import React, { useContext, useEffect, useState } from "react"
import "react-datepicker/dist/react-datepicker.css"
import DatePicker from "react-datepicker"
import DeleteOutOfOfficeModal from "./DeleteOutOfOfficeModal"
import InfoModal from "../../InfoModal"
import Loader from "../../ui/loader"
import moment from "moment-timezone"
import { Button } from "../../ui/button"
import { MeetingRequests } from "../../../api/app.service"
import {
  SvgFilledInformationCircle,
  SvgClosedCalendar,
  SvgEdit,
  SvgClose,
  SvgClock,
  SvgCheckbox,
  SvgCheckboxOff,
  SvgWarning,
  SvgCheckCircle,
  SvgPlus,
} from "../../icons"
import { useToast } from "../../../hooks/useToast"
import { UserCtx } from "../../../context/userContext"

interface Props {
  calendar: any
}

interface Range {
  from: Date
  to: Date
  allDay?: boolean
  meeting_id?: number | null
}

const OutOfOffice: React.FC<Props> = ({ calendar }) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const [outOfOfficeDetails, setOutOfOfficeDetails] = useState(false)
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [editAllDay, setEditAllDay] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [loading, setLoading] = useState(false)
  const [savedOutOfOfficeRanges, setSavedOutOfOfficeRanges] = useState<Range[]>(
    []
  )

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [activeDate, setActiveDate] = useState<Date>(
    moment()
      .startOf("day")
      .tz(user?.timezone?.value ?? "UTC")
      .toDate()
  )
  const [showDeleteDialogueModal, setShowDeleteDialogueModal] = useState(false)
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null)

  const addOneYear = (date: Date) => {
    const newDate = new Date(date)
    newDate.setFullYear(newDate.getFullYear() + 1)
    return newDate
  }

  const formatDateRange = (from: Date, to: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }

    const formatDate = (date: Date) =>
      date.toLocaleString(undefined, options).replace(" at ", " @ ")

    return (
      <div className="flex flex-col items-center text-center md:flex-row md:items-center lg:text-left">
        <span>{formatDate(from)}</span>
        <span className="md:mx-2 font-semibold lowercase">to</span>
        <span>{formatDate(to)}</span>
      </div>
    )
  }

  const getMeetings = async () => {
    if (!user) return

    try {
      const startOfActiveDate = moment(activeDate)
        .startOf("day")
        .utc()
        .tz(user.timezone.value)
        .toISOString()
      const endOfSixMonths = moment(activeDate)
        .add(6, "month")
        .endOf("day")
        .utc()
        .tz(user.timezone.value)
        .toISOString()

      const data = await MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })

      if (data && Array.isArray(data.meetings)) {
        const oooRanges = data.meetings
          .filter((meeting) => meeting.status === "OOO")
          .map((meeting) => ({
            meeting_id: meeting.meeting_id,
            from: moment
              .utc(meeting.start_time)
              .tz(user.timezone.value)
              .toDate(),
            to: moment.utc(meeting.end_time).tz(user.timezone.value).toDate(),
            allDay: false,
          }))
        setSavedOutOfOfficeRanges(oooRanges)
      } else {
        console.error("Invalid meetings data format", data)
        setSavedOutOfOfficeRanges([])
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "An error occurred")
    }
  }

  const handleSave = async (index: number | null) => {
    if (!fromDate || !toDate) {
      setErrorMessage("Please select both From and To dates")
      return
    }
    if (toDate < fromDate) {
      setErrorMessage("To date must be after From date")
      return
    }

    let start = fromDate
    let end = toDate

    if (editAllDay) {
      start = moment(fromDate).startOf("day").set({ millisecond: 0 }).toDate()
      end = moment(toDate)
        .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
        .toDate()
    }

    setErrorMessage("")
    setLoading(true)

    const meeting_id =
      index !== null && index >= 0 && savedOutOfOfficeRanges[index]
        ? savedOutOfOfficeRanges[index].meeting_id || null
        : null

    const toISOStringWithoutMs = (date: Date) =>
      date.toISOString().replace(/\.\d{3}Z$/, "Z")

    const payload = {
      user_id: parseInt(user.user_id),
      meeting_details: {
        meeting_id,
        start_time: toISOStringWithoutMs(start),
        end_time: toISOStringWithoutMs(end),
      },
    }

    try {
      const response = await MeetingRequests.manageOutOfOffice(payload)
      const returnedMeetingId = response.meeting_id || meeting_id

      setSavedOutOfOfficeRanges((prev) => {
        if (index !== null && index >= 0) {
          const updated = [...prev]
          updated[index] = {
            from: start,
            to: end,
            allDay: editAllDay,
            meeting_id: returnedMeetingId,
          }
          return updated
        } else {
          return [
            ...prev,
            {
              from: start,
              to: end,
              allDay: editAllDay,
              meeting_id: returnedMeetingId,
            },
          ]
        }
      })

      resetForm()

      await getMeetings()
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || "Failed to save Out of Office"
      )
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = async () => {
    if (deleteIndex === null) return

    setLoading(true)
    try {
      const rangeToDelete = savedOutOfOfficeRanges[deleteIndex]
      const meetingId = rangeToDelete.meeting_id

      if (meetingId) {
        await MeetingRequests.manageOutOfOffice({
          user_id: parseInt(user.user_id),
          meeting_details: {
            meeting_id: meetingId,
            start_time: null,
            end_time: null,
          },
        })
      }

      setSavedOutOfOfficeRanges((prev) =>
        prev.filter((_, i) => i !== deleteIndex)
      )
      resetForm()
      setShowDeleteDialogueModal(false)
      setDeleteIndex(null)
      toast.success("Out of Office deleted successfully.")
    } catch (error) {
      toast.error("Failed to delete Out of Office.")
    } finally {
      setLoading(false)
    }
  }

  const promptDelete = (index: number) => {
    setShowDeleteDialogueModal(true)
    setDeleteIndex(index)
  }

  useEffect(() => {
    if (calendar.profileCount > 0) {
      getMeetings()
    }
  }, [calendar])

  const handleEdit = (index: number) => {
    const range = savedOutOfOfficeRanges[index]
    setFromDate(range.from)
    setToDate(range.to)
    setEditAllDay(!!range.allDay)
    setEditingIndex(index)
    setErrorMessage("")
  }

  const resetForm = () => {
    setFromDate(null)
    setToDate(null)
    setEditingIndex(null)
    setEditAllDay(false)
  }

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col px-[20px] md:pt-0 bg-[white] rounded-xl main-shadow ">
      <div className="flex flex-col p-6 mt-4 mb-4">
        <div className="flex items-center space-x-2 pr-2">
          <div className="w-full text-left text-xl font-bold">
            Out of Office Hours
            <Button
              onClick={() => setOutOfOfficeDetails(true)}
              variant="ghost"
              size="icon"
            >
              <SvgFilledInformationCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="bg-grayMist w-full h-[1px]"></div>

        {savedOutOfOfficeRanges.length > 0 && (
          <div className="mt-2 space-y-4">
            {savedOutOfOfficeRanges.map(
              ({ from, to, allDay, meeting_id }, idx) => (
                <div
                  className="flex flex-col md:flex-row md:items-center md:space-x-2 mt-4 mb-2"
                  key={idx}
                >
                  {editingIndex === idx ? (
                    <div className="flex flex-col w-full md:flex-row md:space-x-4">
                      <div className="flex items-center space-x-2 mb-2 md:mb-0">
                        <span className="w-6 flex justify-center">
                          <SvgClock />
                        </span>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          showTimeSelect={!editAllDay}
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat={
                            editAllDay ? "MMMM d, yyyy" : "MMMM d, yyyy h:mm aa"
                          }
                          minDate={new Date()}
                          maxDate={addOneYear(new Date())}
                          className="border rounded-md px-3 py-1 w-full"
                          placeholderText="Select start date"
                        />
                      </div>

                      <div className="flex items-center space-x-2 mb-2 md:mb-0">
                        <span className="w-6 flex justify-center md:justify-start font-semibold lowercase">
                          to
                        </span>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          showTimeSelect={!editAllDay}
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat={
                            editAllDay ? "MMMM d, yyyy" : "MMMM d, yyyy h:mm aa"
                          }
                          minDate={fromDate || new Date()}
                          maxDate={addOneYear(new Date())}
                          className="border rounded-md px-3 py-1 w-full"
                          placeholderText="Select end date"
                        />
                      </div>
                      {/* All Day & Buttons */}
                      <div className="flex flex-col md:flex-row md:items-center md:space-x-2 mt-2 md:mt-0">
                        {" "}
                        {/* Checkbox + label */}
                        <div className="flex items-center space-x-2 mb-2 md:mb-0 whitespace-nowrap cursor-pointer select-none">
                          {editAllDay ? (
                            <SvgCheckbox
                              className="flex-none w-5 h-5"
                              onClick={() => setEditAllDay(!editAllDay)}
                            />
                          ) : (
                            <SvgCheckboxOff
                              fill="#E8EFF5"
                              className="flex-none w-5 h-5 border border-[gray] rounded-md"
                              onClick={() => setEditAllDay(!editAllDay)}
                            />
                          )}
                          <span>All Day</span>
                        </div>
                        {/* Buttons */}
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            onClick={() => handleSave(idx)}
                          >
                            {loading ? <Loader /> : "Update"}
                          </Button>
                          <Button
                            variant="secondary"
                            disabled={loading}
                            onClick={resetForm}
                          >
                            {loading ? <Loader /> : "Cancel"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4 mb-2">
                      <SvgClosedCalendar className="w-6 h-6 mr-2" />
                      <div className="text-lg">{formatDateRange(from, to)}</div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(idx)}
                      >
                        <SvgEdit />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => promptDelete(idx)}
                      >
                        <SvgClose />
                      </Button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}

        {editingIndex === null && (
          <Button
            variant="outline"
            className="mt-6 w-max"
            onClick={() => setEditingIndex(-1)}
          >
            <SvgPlus /> Add Out of Office
          </Button>
        )}

        {editingIndex === -1 && (
          <div className="flex flex-col w-full md:flex-row md:space-x-4 mt-4">
            <div className="flex items-center space-x-2 mb-2 md:mb-0">
              <span className="w-6 flex justify-center">
                <SvgClock />
              </span>
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                showTimeSelect={!editAllDay}
                timeIntervals={15}
                timeCaption="Time"
                dateFormat={
                  editAllDay ? "MMMM d, yyyy" : "MMMM d, yyyy h:mm aa"
                }
                minDate={new Date()}
                maxDate={addOneYear(new Date())}
                className="border rounded-md px-3 py-1 w-full"
                placeholderText="Select start date"
              />
            </div>

            {/* To date */}
            <div className="flex items-center space-x-2 mb-2 md:mb-0">
              <span className="w-6 flex justify-center md:justify-start font-semibold lowercase">
                to
              </span>
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                showTimeSelect={!editAllDay}
                timeIntervals={15}
                dateFormat={
                  editAllDay ? "MMMM d, yyyy" : "MMMM d, yyyy h:mm aa"
                }
                minDate={fromDate || new Date()}
                maxDate={addOneYear(new Date())}
                className="border rounded-md px-3 py-1 w-full"
                placeholderText="Select end date"
              />
            </div>

            {/* All Day & Buttons */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-2 mt-2 md:mt-0 flex-none w-full md:w-auto">
              {/* Checkbox */}
              <div className="flex items-center space-x-2 cursor-pointer select-none mb-2 md:mb-0 whitespace-nowrap">
                {editAllDay ? (
                  <SvgCheckbox
                    className="flex-none w-5 h-5"
                    onClick={() => setEditAllDay(!editAllDay)}
                  />
                ) : (
                  <SvgCheckboxOff
                    fill="#E8EFF5"
                    className="flex-none w-5 h-5 border border-[gray] rounded-md"
                    onClick={() => setEditAllDay(!editAllDay)}
                  />
                )}
                <span>All Day</span>
              </div>

              {/* Buttons */}
              <div className="flex space-x-2">
                <Button variant="default" onClick={() => handleSave(null)}>
                  <SvgCheckCircle /> {loading ? <Loader /> : "Save"}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  {loading ? <Loader /> : "Cancel"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="flex items-center text-red-600 mt-1 font-semibold">
            <SvgWarning className="w-4 h-4 mr-2" />
            {errorMessage}
          </div>
        )}
        {outOfOfficeDetails && (
          <InfoModal
            title="Effortless Out of Office Settings"
            description={
              <div className="">
                <p>
                  <b>Protect your personal time with confidence.</b> Set
                  yourself as unavailable and let Zoee automatically prevent
                  client bookings during your breaks, vacations, and personal
                  time.
                </p>
                <p className="mt-6">How it works:</p>
                <ul className="list-disc ml-5 mb-6">
                  <li>
                    <b>Block specific dates or entire days</b> — with just a few
                    taps
                  </li>
                  <li>
                    <b>Automatic scheduling protection</b> — no more surprise
                    bookings during your time off
                  </li>
                  <li>
                    <b>Update anytime</b> — easily modify or remove your Out of
                    Office settings as plans change
                  </li>
                </ul>
                <p>
                  Your well-deserved breaks are now truly yours. Zoee keeps your
                  schedule protected so you can disconnect completely, knowing
                  your clients will only see your available times.
                </p>
                <p className="italic mt-4">
                  Because time off should actually be time off.
                </p>
              </div>
            }
            setShowModal={setOutOfOfficeDetails}
            textAlign="left"
          />
        )}
        {showDeleteDialogueModal && deleteIndex !== null && (
          <DeleteOutOfOfficeModal
            setShowDeleteDialogueModal={setShowDeleteDialogueModal}
            outOfOfficeRange={savedOutOfOfficeRanges[deleteIndex]}
            onDelete={confirmDelete}
            loading={loading}
          />
        )}
      </div>
    </div>
  )
}

export default OutOfOffice
